import React, { useEffect, useState } from 'react';
import {
  useRecoilState,
  useRecoilValueLoadable,
  useSetRecoilState
} from 'recoil';
import {
  Image,
  Button,
  Popconfirm,
  notification,
  Avatar,
  Tooltip,
  Table
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';
import {
  profileAtom,
  selectedSectionAtom,
  reloadSectionsAtom,
  selectedDepartmentOrSectionForLogsAtom,
  showDepartmentsSectionsModalAtom
} from '../../../atoms/Atoms';
import api from '../../../api/api';
import EditSectionDrawer from './shared-components/EditSectionDrawer';
import LogsModal from './shared-components/LogsModal';
import DepartmentCategoriesBreakdownModal from './shared-components/DepartmentCategoriesBreakdownModal';

function Sections() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const userProfile = useRecoilValueLoadable(profileAtom);
  const isAdmin =
    userProfile.contents && userProfile?.contents?.roles?.includes('SuperAdmin');
  const isCustomerService =
    isAdmin || (userProfile?.contents && (userProfile?.contents?.roles?.includes('CustomerServiceAdmin') || userProfile?.contents?.roles?.includes('CustomerService')));
  const isStoreManager =
    isAdmin || isCustomerService || (userProfile?.contents && (userProfile?.contents?.roles?.includes('StoreManager') || userProfile?.contents?.roles?.includes('StoreAdmin')));
  if (!isAdmin && !isCustomerService && !isStoreManager) {
    history.push('/');
    return null;
  }

  const setSelectedSection = useSetRecoilState(selectedSectionAtom);
  const [reloadSections, setReloadSections] = useRecoilState(reloadSectionsAtom);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const setSelectedDepartmentOrSectionForLogsAtom = useSetRecoilState(selectedDepartmentOrSectionForLogsAtom);
  const setShowDepartmentsSectionsModal = useSetRecoilState(showDepartmentsSectionsModalAtom);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function setLoadingInTable(id, load) {
    const temp = JSON.parse(JSON.stringify(sections));
    for (let i = 0; i < temp.length; i++) {
      if (temp[i].id === id) {
        temp[i].loading = load;
      }
    }
    setSections(temp);
  }

  function getSections() {
    setLoading(true);
    api.get('businesses/menusections')
      .then((response) => {
        setLoading(false);
        if (response.data.error) {
          showMessage(response.data.error);
        } else {
          setSections(response.data.data.results);
        }
      })
      .catch((err) => {
        setLoading(false);
        showMessage(err.message);
      });
  }

  function remove(id) {
    setLoadingInTable(id, true);
    api.delete(`businesses/menusections/${id}`)
      .then((response) => {
        setLoading(false);
        if (response.data.error) {
          showMessage(response.data.error);
        } else {
          getSections();
        }
      })
      .catch((err) => {
        setLoadingInTable(id, false);
        showMessage(err.message);
      });
  }

  useEffect(() => {
    getSections();
    setSelectedSection(null);
  }, []);

  useEffect(() => {
    if (reloadSections) {
      getSections();
      setReloadSections(false);
    }
  }, [reloadSections]);

  return (
    <div>
      <PageHeader
        title={t('categories')}
        extra={[
          <Button
            key="viewdeptcatdetails"
            type="primary"
            size="small"
            onClick={() => {
              setShowDepartmentsSectionsModal(true);
            }}
          >
            {t('view_departments_categories')}
          </Button>,
          <Button
            key="create"
            type="primary"
            size="small"
            onClick={() => {
              setSelectedSection({
                color: '#ffffff',
                borderColor: '#ffffff'
              });
            }}
          >
            {t('create')}
          </Button>,
          <Button
            key="reload"
            type="primary"
            size="small"
            onClick={() => {
              setReloadSections(true);
            }}
          >
            {t('reload')}
          </Button>
        ]}
      />
      <Table
        size="small"
        bordered
        scroll={{ x: 500 }}
        loading={loading}
        pagination={false}
        rowKey="id"
        dataSource={sections}
        columns={[
          {
            title: t('image'),
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div
                className="rounded-lg p-5 border-4"
                style={{
                  background: row.color,
                  borderColor: row.borderColor
                }}
              >
                <Image src={row.imageUrl} width={75} />
              </div>
            ),
          },
          {
            title: t('name'),
            align: 'center',
            className: 'text-xs',
            sorter: (a, b) => (i18n.language === 'en' ? a.nameEng : a.nameSpa).localeCompare((i18n.language === 'en' ? b.nameEng : b.nameSpa)),
            render: (row) => (
              <span>{i18n.language === 'en' ? row.nameEng : row.nameSpa}</span>
            ),
          },
          {
            title: t('department'),
            align: 'center',
            className: 'text-xs',
            sorter: (a, b) => (i18n.language === 'en' ? a.superSectionNameEng : a.superSectionName).localeCompare((i18n.language === 'en' ? b.superSectionNameEng : b.superSectionName)),
            render: (row) => (
              <span>{i18n.language === 'en' ? row.superSectionNameEng : row.superSectionName}</span>
            ),
          },
          {
            title: '',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div className="space-x-2">
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    setSelectedSection(row);
                  }}
                >
                  {t('edit')}
                </Button>
                <Popconfirm
                  title={t('delete_section_prompt')}
                  onConfirm={() => remove(row.id)}
                  okText={t('yes')}
                  cancelText={t('no')}
                >
                  <Button
                    loading={row.loading}
                    disabled={row.loading}
                    size="small"
                    type="primary"
                    danger
                  >
                    {t('remove')}
                  </Button>
                </Popconfirm>
                {isAdmin && (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      setSelectedDepartmentOrSectionForLogsAtom({ sectionId: row.id });
                    }}
                  >
                    {t('logs')}
                  </Button>
                )}
              </div>
            ),
          }
        ]}
      />
      <EditSectionDrawer />
      <LogsModal />
      <DepartmentCategoriesBreakdownModal />
    </div>
  );
}

export default Sections;
